/**
 * Asking a client for documents.
 *
 * The start of the whole client-facing flow: until the firm asks, the client's
 * portal has nothing to show them and nothing to do. This was the missing piece —
 * every endpoint behind it existed and was tested, and there was no way to reach
 * any of them.
 *
 * Two ways to ask, because firms do both:
 *
 * **The checklist** raises everything the matter's service configuration
 * requires, in one call. Onboarding asks for six documents far more often than
 * one, and doing that as six separate actions is how half a checklist ends up
 * created. The categories come from the service, never from this screen — a
 * caller-supplied list could ask a client for something the firm's own
 * configuration never requires.
 *
 * **One at a time** for the thing nobody anticipated.
 *
 * Instructions matter more than they look. "Proof of address" means nothing to
 * somebody who has never given one to a solicitor; "a bank statement or utility
 * bill dated in the last three months, showing your name and address" is the
 * difference between one upload and four.
 */

import { userMessage } from '@legalcrm/api/errors';
import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type DocumentRequest,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormCheckbox,
  FormDate,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  StatusPill,
  statusTone,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { useAuth } from '@/auth/AuthProvider';
import { Can } from '@/auth/permissions';
import { formatDate, humanise, isPast } from '@/lib/format';

interface Category {
  id: string;
  key: string;
  name: string;
}

const schema = z.object({
  category: z.string().min(1, 'Choose what you are asking for'),
  title: z.string(),
  instructions: z.string(),
  due_at: z.string(),
  is_mandatory: z.boolean(),
});

type Values = z.infer<typeof schema>;

export interface RequestDocumentsPanelProps {
  clientId: string;
  caseId?: string;
}

export function RequestDocumentsPanel({ clientId, caseId }: RequestDocumentsPanelProps) {
  const api = useApi();
  const [asking, setAsking] = useState(false);
  const [cancelling, setCancelling] = useState<DocumentRequest | null>(null);
  const { toast } = useToast();
  const { matterLabel } = useAuth();

  // The request is made either way -- a firm may prepare before inviting anybody. What
  // must not happen is somebody believing the client has been asked while their app shows
  // nothing, which is exactly what happened before the server started saying so.
  const warnIfUnseen = (clientCanSee: boolean | undefined) => {
    if (clientCanSee !== false) return;
    toast({
      title: 'Asked, but the client cannot see it yet',
      description: caseId
        ? `Nobody at this client can see this ${matterLabel.toLowerCase()} in their portal, so the request will not appear in their app. Give a contact access on the client’s Portal access tab.`
        : 'Nobody at this client has been invited to the portal, so the request will not appear in any app. Invite a contact on the client’s Portal access tab.',
      tone: 'warning',
    });
  };

  const list = usePaginatedList<DocumentRequest>(
    qk.list('document-requests', { client: clientId, case: caseId }),
    STAFF.documentRequests,
    { client: clientId, case: caseId },
  );

  const categories = useApiQuery<{ results: Category[] } | Category[]>(
    qk.list('document-categories'),
    STAFF.documentCategories,
  );
  const categoryRows = Array.isArray(categories.data)
    ? categories.data
    : (categories.data?.results ?? []);

  const invalidates = [qk.all('document-requests'), qk.all('documents')];

  const form = useZodForm(schema, {
    defaultValues: {
      category: '',
      title: '',
      instructions: '',
      due_at: '',
      is_mandatory: true,
    },
  });

  const ask = useApiMutation<DocumentRequest, Values>(
    {
      mutationFn: (values) =>
        api.post<DocumentRequest>(`${STAFF.documentRequests}/`, {
          client: clientId,
          case: caseId,
          category: values.category,
          title: values.title || undefined,
          instructions: values.instructions || undefined,
          // End of day, so "by Friday" means what a person means by it rather than
          // midnight, which would read as overdue for the whole of Friday.
          due_at: values.due_at ? `${values.due_at}T17:00:00Z` : undefined,
          is_mandatory: values.is_mandatory,
        }),
      invalidates,
    },
    { onSuccess: (created) => warnIfUnseen(created.client_can_see) },
  );

  const raiseChecklist = useApiMutation<{ client_can_see?: boolean }, void>(
    {
      mutationFn: () =>
        api.post<{ client_can_see?: boolean }>(`${STAFF.documentRequestsBulk}/`, {
          case: caseId,
        }),
      invalidates,
    },
    { onSuccess: (result) => warnIfUnseen(result.client_can_see) },
  );

  const remind = useApiMutation<unknown, string>({
    mutationFn: (id) =>
      api.post(`${STAFF.documentRequestRemind(id)}/`, {}, {
        // Reminding sends something to a person, so a retry must not send it twice.
        idempotencyKey: `remind-${id}-${new Date().toISOString().slice(0, 10)}`,
      }),
    invalidates,
  });

  // A reason is required by the server, and rightly: a client saw this on their
  // outstanding list, and "why did that disappear" is a question the file should be
  // able to answer months later.
  const cancel = useApiMutation<unknown, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      api.post(`${STAFF.documentRequestCancel(id)}/`, { reason }),
    invalidates,
  });

  const cancelForm = useZodForm(
    z.object({ reason: z.string().min(1, 'Say why you are no longer asking') }),
    { defaultValues: { reason: '' } },
  );

  return (
    <>
      <Card>
        <CardHeader
          title="Asked of the client"
          actions={
            <Can module="document" action="create">
              <div className="flex gap-1.5">
                {caseId && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={raiseChecklist.isPending}
                    onClick={() => raiseChecklist.mutate()}
                  >
                    Raise checklist
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>
                  Ask for a document
                </Button>
              </div>
            </Can>
          }
        />

        {list.items.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nothing asked for yet"
            description="The client's portal shows them what you ask for here."
          />
        ) : (
          <ul className="flex flex-col divide-y">
            {list.items.map((request) => {
              const overdue =
                request.status === 'outstanding' && isPast(request.due_at);
              return (
                <li key={request.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-ink">{request.title}</p>
                      {request.is_mandatory && (
                        <Badge tone="warning" size="sm">
                          Required
                        </Badge>
                      )}
                    </div>
                    {request.instructions && (
                      <p className="truncate text-xs text-ink-muted">
                        {request.instructions}
                      </p>
                    )}
                    {request.due_at && (
                      <p
                        className={
                          overdue ? 'text-xs text-danger' : 'text-xs text-ink-subtle'
                        }
                      >
                        Due {formatDate(request.due_at)}
                      </p>
                    )}
                  </div>

                  <StatusPill
                    label={humanise(request.status)}
                    tone={statusTone(request.status)}
                  />

                  {request.status === 'outstanding' && (
                    <Can module="document" action="create">
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={remind.isPending}
                          onClick={() => remind.mutate(request.id)}
                        >
                          Remind
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCancelling(request)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Can>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {Boolean(raiseChecklist.error || remind.error || cancel.error) && (
          <CardBody>
            {/* The server's own words. A 409 here says something actionable -- the
                checklist was already raised, the request is already fulfilled --
                and inventing a friendlier sentence would hide it. */}
            <p role="alert" className="text-sm text-danger">
              {userMessage(raiseChecklist.error ?? remind.error ?? cancel.error)}
            </p>
          </CardBody>
        )}
      </Card>

      <Modal open={asking} onClose={() => setAsking(false)} title="Ask for a document">
        <Form
          form={form}
          ariaLabel="Ask for a document"
          error={ask.error}
          onSubmit={async (values) => {
            await ask.mutateAsync(values);
            form.reset();
            setAsking(false);
          }}
        >
          <FormSelect
            form={form}
            name="category"
            label="What are you asking for"
            placeholder="Choose a category"
            options={categoryRows.map((row) => ({ value: row.id, label: row.name }))}
          />

          <FormInput
            form={form}
            name="title"
            label="Call it"
            hint="Optional. The category name is used if you leave this empty."
          />

          <FormTextarea
            form={form}
            name="instructions"
            label="What the client needs to do"
            rows={3}
            hint="Worth writing properly. Proof of address means nothing to somebody who has never been asked for one."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormDate form={form} name="due_at" label="Needed by" />
            <FormCheckbox
              form={form}
              name="is_mandatory"
              label="Required"
              hint="A required document can hold up the matter."
            />
          </div>

          <FormActions
            submitLabel="Ask the client"
            busy={ask.isPending}
            onCancel={() => setAsking(false)}
          />
        </Form>
      </Modal>

      <Modal
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        title={`Stop asking for ${cancelling?.title ?? 'this'}?`}
      >
        <Form
          form={cancelForm}
          ariaLabel="Stop asking for this document"
          error={cancel.error}
          onSubmit={async (values) => {
            if (!cancelling) return;
            await cancel.mutateAsync({ id: cancelling.id, reason: values.reason });
            cancelForm.reset();
            setCancelling(null);
          }}
        >
          <p className="text-sm text-ink-muted">
            It disappears from the client&rsquo;s outstanding list. The record that it
            was asked for, and why you stopped, stays on the file.
          </p>

          <FormTextarea
            form={cancelForm}
            name="reason"
            label="Why are you no longer asking"
            rows={3}
            autoFocus
          />

          <FormActions
            submitLabel="Stop asking"
            busy={cancel.isPending}
            onCancel={() => setCancelling(null)}
          />
        </Form>
      </Modal>

    </>
  );
}
